class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' || event.code === 'Escape') this.close();
    });
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();
    if (!window.flamengoBfCartTriggerInstalled) {
      window.flamengoBfCartTriggerInstalled = true;
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('#cart-icon-bubble');
        const drawer = document.querySelector('cart-drawer');
        if (!trigger || !drawer) return;
        event.preventDefault();
        drawer.open(trigger);
      });
      document.addEventListener('keydown', (event) => {
        if (event.code !== 'Space' || !event.target.closest('#cart-icon-bubble')) return;
        event.preventDefault();
        document.querySelector('cart-drawer')?.open(event.target.closest('#cart-icon-bubble'));
      });
    }
  }

  connectedCallback() {
    if (!this.classList.contains('active')) {
      this.setAttribute('aria-hidden', 'true');
      this.inert = true;
    }
  }

  setHeaderCartIconAccessibility() {
    const link = document.getElementById('cart-icon-bubble');
    if (!link) return;
    link.setAttribute('role', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('aria-controls', 'CartDrawer');
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    else if (!this.activeElement && !this.contains(document.activeElement)) this.setActiveElement(document.activeElement);
    window.clearTimeout(this.focusTimer);
    this.inert = false;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('bf-cart-open');
    const focus = () => {
      if (!this.classList.contains('active')) return;
      const panel = this.querySelector('.drawer__inner');
      if (panel) trapFocus(panel, panel.querySelector('.drawer__close') || panel);
    };
    this.focusTimer = window.setTimeout(focus, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 260);
  }

  close() {
    window.clearTimeout(this.focusTimer);
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    const returnTo = this.activeElement?.isConnected ? this.activeElement : document.getElementById('cart-icon-bubble');
    removeTrapFocus(returnTo);
    this.inert = true;
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('bf-cart-open');
  }

  renderContents(state, options = {}) {
    const html = state.sections?.['cart-drawer'];
    const source = html ? this.getSectionDOM(html, '#CartDrawer') : null;
    const target = this.querySelector('#CartDrawer');
    if (!source || !target) return false;
    const scroll = this.querySelector('[data-bf-cart-scroll]')?.scrollTop || 0;
    const activeId = this.contains(document.activeElement) ? document.activeElement.id : '';
    const alreadyOpen = this.classList.contains('active');
    target.innerHTML = source.innerHTML;
    this.classList.toggle('is-empty', !source.querySelector('.cart-item'));
    const bubbleHtml = state.sections?.['cart-icon-bubble'];
    if (bubbleHtml) {
      const bubbleDoc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
      const bubbleSource = bubbleDoc.querySelector('.shopify-section') || bubbleDoc.querySelector('#cart-icon-bubble');
      const bubbleTarget = document.getElementById('cart-icon-bubble');
      if (bubbleTarget && bubbleSource) bubbleTarget.innerHTML = bubbleSource.innerHTML;
    }
    this.setHeaderCartIconAccessibility();
    const body = this.querySelector('[data-bf-cart-scroll]');
    if (body && options.preserveScroll) body.scrollTop = scroll;
    if (options.open !== false && !alreadyOpen) this.open();
    else if (alreadyOpen) {
      window.clearTimeout(this.focusTimer);
      const panel = this.querySelector('.drawer__inner');
      const previousFocus = activeId ? this.querySelector('#' + CSS.escape(activeId)) : null;
      if (panel) trapFocus(panel, previousFocus || panel.querySelector('.drawer__close') || panel);
    }
    return true;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onChange(event) {
    if (!event.target.matches('.quantity__input')) return;
    if (Number(event.target.value) === 0) {
      this.updateQuantity(event.target.dataset.index, 0, event, event.target.name, event.target.dataset.quantityVariantId);
      return;
    }
    this.validateQuantity(event);
  }

  resetQuantityInput(index) {
    const input = this.querySelector('#Drawer-quantity-' + index);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }

  async updateQuantity(line, quantity, event, name, variantId) {
    const drawer = this.closest('cart-drawer');
    const input = this.querySelector('#Drawer-quantity-' + line);
    const row = this.querySelector('#CartDrawer-Item-' + line);
    const key = row?.dataset.bfCartLine;
    if (!drawer || !key || drawer.classList.contains('is-updating')) return;
    if (!Number.isInteger(Number(quantity)) || Number(quantity) < 0) {
      this.resetQuantityInput(line);
      return;
    }
    drawer.classList.add('is-updating');
    this.setAttribute('aria-busy', 'true');
    const errorBox = this.querySelector('#CartDrawer-CartErrors');
    if (errorBox) errorBox.textContent = '';
    const activeId = document.activeElement?.id;
    try {
      const response = await fetch(routes.cart_change_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(quantity), sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname })
      });
      const state = await response.json();
      if (!response.ok || state.errors || state.status) throw new Error(state.description || state.errors || state.message || window.cartStrings.error);
      if (!drawer.renderContents(state, { preserveScroll: true })) {
        window.location.assign(routes.cart_url);
        return;
      }
      const status = drawer.querySelector('#CartDrawer-LiveRegionText');
      const newLine = state.items?.find((item) => String(item.variant_id) === String(variantId));
      if (status && quantity > 0 && newLine && newLine.quantity !== Number(quantity)) status.textContent = window.cartStrings.quantityError.replace('[quantity]', newLine.quantity);
      const focused = activeId ? drawer.querySelector('#' + CSS.escape(activeId)) : null;
      if (focused && quantity > 0) focused.focus({ preventScroll: true });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'flamengo-bf-cart', cart: state } }));
    } catch (error) {
      if (input) input.value = input.getAttribute('value');
      if (errorBox) errorBox.textContent = error.message || window.cartStrings.error;
    } finally {
      drawer.classList.remove('is-updating');
      this.removeAttribute('aria-busy');
    }
  }

  async onCartUpdate() {
    const drawer = this.closest('cart-drawer');
    if (!drawer || drawer.classList.contains('is-updating')) return;
    try {
      const response = await fetch(routes.cart_url + '?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error();
      const sections = await response.json();
      drawer.renderContents({ sections }, { open: drawer.classList.contains('active'), preserveScroll: true });
    } catch (_) {
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = window.cartStrings.error;
    }
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);
